import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface OnboardingTourContextType {
  isOnboardingActive: boolean;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  completeOnboarding: () => void;
}

export const OnboardingTourContext = createContext<OnboardingTourContextType | undefined>(undefined);

export const ONBOARDING_COMPLETE_KEY = 'macc-beer-fest-onboarding-complete';

export const ONBOARDING_STEP_INDEXES = {
  selectionActions: 2,
  tastingListBadge: 3,
  badges: 4,
  complete: 5,
} as const;

interface OnboardingTourProviderProps {
  children: ReactNode;
}

/**
 * Provider for onboarding tour state
 * Allows beer cards to know when to show demo icons
 */
export const OnboardingTourProvider: React.FC<OnboardingTourProviderProps> = ({ children }) => {
  const [isOnboardingActive, setIsOnboardingActive] = useState(() => {
    try {
      return localStorage.getItem(ONBOARDING_COMPLETE_KEY) !== 'true';
    } catch {
      return true;
    }
  });
  
  const [currentStep, setCurrentStep] = useState(0);
  
  const completeOnboarding = useCallback(() => {
    // Reset step to 0 first to clear demo icons immediately
    setCurrentStep(0);
    setIsOnboardingActive(false);
    try {
      localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
    } catch {
      // Ignore localStorage errors
    }
  }, []);
  
  return (
    <OnboardingTourContext.Provider value={{
      isOnboardingActive,
      currentStep,
      setCurrentStep,
      completeOnboarding,
    }}>
      {children}
    </OnboardingTourContext.Provider>
  );
};

/**
 * Hook to access onboarding tour state
 */
export const useOnboardingTour = (): OnboardingTourContextType => {
  const context = useContext(OnboardingTourContext);
  if (!context) {
    throw new Error('useOnboardingTour must be used within an OnboardingTourProvider');
  }
  return context;
};

/**
 * Hook to check if demo icons should be shown on a beer card
 * Returns true for the first rendered beer when on relevant tour steps
 */
export const useDemoIconsForBeer = (beerId: string, renderIndex?: number): {
  showDemoTried: boolean;
  showDemoFavourite: boolean;
  showDemoTastingList: boolean;
  forceExpanded: boolean;
  forceSelected: boolean;
  isFirstRenderedBeer: boolean;
} => {
  const context = useContext(OnboardingTourContext);
  
  // If not in context or onboarding not active, no demo icons
  if (!context || !context.isOnboardingActive) {
    return {
      showDemoTried: false,
      showDemoFavourite: false,
      showDemoTastingList: false,
      forceExpanded: false,
      forceSelected: false,
      isFirstRenderedBeer: false,
    };
  }
  
  // Only show demo icons on the first rendered beer (index 0)
  const isFirstRenderedBeer = renderIndex === 0;
  
  if (!isFirstRenderedBeer) {
    return {
      showDemoTried: false,
      showDemoFavourite: false,
      showDemoTastingList: false,
      forceExpanded: false,
      forceSelected: false,
      isFirstRenderedBeer: false,
    };
  }
  
  // Tour steps (0-indexed):
  // 0 = welcome
  // 1 = navigation-tabs
  // 2 = selection-actions
  // 3 = tasting-list-badge
  // 4 = badges
  // 5 = complete
  const { currentStep } = context;
  
  // Force select the first beer card during the selection actions step.
  const forceSelected = currentStep === ONBOARDING_STEP_INDEXES.selectionActions;
  
  // No rating/notes steps in the current tour flow.
  const forceExpanded = false;
  
  // Show tasting list badge during the tasting-list-badge step.
  const showDemoTastingList = currentStep === ONBOARDING_STEP_INDEXES.tastingListBadge;
  
  // Show all badges during the badges step.
  const showDemoBadges = currentStep === ONBOARDING_STEP_INDEXES.badges;
  
  return {
    showDemoTried: false, // Not used anymore
    showDemoFavourite: showDemoBadges,
    showDemoTastingList,
    forceExpanded,
    forceSelected,
    isFirstRenderedBeer,
  };
};
